import { getValidAccessToken, refreshToken, getSupabase } from '../auth/office-auth.service';

/**
 * Centralized API client with automatic token refresh on 401.
 *
 * Replaces the per-file getAuthHeaders() + bare fetch() pattern.
 * On a 401 response, refreshes the Supabase token and retries once
 * before throwing — preventing unnecessary logouts on token expiry.
 */

async function getAccessToken(): Promise<string> {
  // getValidAccessToken already refreshes if within 60s of expiry
  const token = await getValidAccessToken();
  if (token) return token;

  // Fallback: try a direct session read (covers edge cases where
  // getValidAccessToken returns null but a valid session still exists)
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;

  throw new Error('Not authenticated');
}

function buildHeaders(
  token: string,
  includeContentType: boolean,
  extra?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    ...extra,
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

/**
 * Return auth headers ready to pass to fetch().
 * Prefers a pre-emptively refreshed token so requests rarely hit 401.
 */
export async function getAuthHeaders(
  includeContentType = true,
): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return buildHeaders(token, includeContentType);
}

export interface ApiRequestOptions extends Omit<RequestInit, 'headers'> {
  /** Extra headers merged on top of auth + content-type. */
  headers?: Record<string, string>;
  /** Set false to omit Content-Type (e.g. FormData uploads). Default true when body is present and is not FormData. */
  includeContentType?: boolean;
}

/**
 * Authenticated fetch with automatic 401 retry.
 *
 * 1. Sends the request with the current access token.
 * 2. On 401, refreshes the token and retries **once**.
 * 3. If the retry also 401s (or refresh fails), throws.
 */
export async function apiFetch(
  url: string,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const { headers: extraHeaders, includeContentType, ...fetchOptions } = options;

  // Auto-detect content type: skip for FormData (browser sets boundary)
  const shouldIncludeContentType =
    includeContentType ?? (fetchOptions.body != null && !(fetchOptions.body instanceof FormData));

  const token = await getAccessToken();
  const headers = buildHeaders(token, shouldIncludeContentType, extraHeaders);

  const response = await fetch(url, { ...fetchOptions, headers });

  if (response.status === 401) {
    // Attempt one token refresh + retry
    const newToken = await refreshToken();
    if (newToken) {
      const retryHeaders = buildHeaders(newToken, shouldIncludeContentType, extraHeaders);
      return fetch(url, { ...fetchOptions, headers: retryHeaders });
    }
  }

  return response;
}

/**
 * Convenience wrapper: apiFetch + JSON parsing + error handling.
 * Returns the parsed JSON body. Throws on non-ok responses.
 */
export async function apiRequest<T = unknown>(
  url: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const response = await apiFetch(url, options);

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Expected JSON but got ${contentType || 'unknown content type'} from ${url}`);
  }

  return response.json();
}
